import * as React from 'react';
import { ChangeEvent, useEffect, useState } from 'react';
import { getNameValidation } from '../../../common/validators';
import { FormattedMessage, useIntl } from 'react-intl';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { TextFormControl } from '../../../common/TextFormControl';
import { patchUserThunk } from '../../thunks';
import { isMaintenanceMode, selectPending, selectUser } from '../../appSlice';
import { renderSpinner } from '../../common';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';

const PasswordResetField = () => (
    <form className={'form-horizontal'}>
        <fieldset>
            <div className={'form-group'} hidden={true}>
                <label className={'control-label padding-top-5'}>User name</label>
                <div className={'position-relative'}>
                    <input type={'text'} autoComplete={'username'} className={'form-control'} readOnly={true} />
                </div>
            </div>
            <div className={'form-group'}>
                <label className={'control-label padding-top-5'}>
                    <FormattedMessage id={'intl-msg:settingsDialog.tab.personalData.label.password'} />
                </label>
                <div className={'position-relative'}>
                    <input
                        type={'password'}
                        autoComplete={'new-password'}
                        className={'form-control'}
                        value={'xxxxxxxx'}
                        readOnly={true}
                    />
                    <span className={'position-absolute top-1 bottom-1 right-1 rounded bg-lightest'}>
                        <a
                            className={'btn btn-link'}
                            href={'https://auth.iam.rio.cloud/forgot-password'}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                        >
                            <FormattedMessage id={'intl-msg:settingsDialog.tab.personalData.label.passwordReset'} />
                        </a>
                    </span>
                </div>
            </div>
        </fieldset>
    </form>
);

export const PersonalData: React.FC = () => {
    const user = useAppSelector(selectUser);
    const isPending = useAppSelector(selectPending);
    const isInMaintenanceMode = useAppSelector(isMaintenanceMode);

    const dispatch = useAppDispatch();
    const intl = useIntl();

    const [firstName, setFirstName] = useState(user.firstName);
    const [lastName, setLastName] = useState(user.lastName);

    useEffect(() => {
        setFirstName(user.firstName);
        setLastName(user.lastName);
    }, [user]);

    const isDisabled = () => {
        if (firstName === user.firstName && lastName === user.lastName) {
            return 'disabled';
        } else if (firstName.trim() === '' || lastName.trim() === '' || isInMaintenanceMode) {
            return 'disabled';
        }
        return '';
    };

    const renderConfirmText = () => {
        const textNode = intl.formatMessage({ id: 'intl-msg:settingsDialog.tab.language.button.save.label' });
        return isPending ? <Spinner text={textNode} /> : textNode;
    };

    const renderSaveButton = () => (
        <div className={'btn-toolbar pull-right margin-top-20 margin-bottom-20'}>
            <div
                className={`btn btn-primary ${isDisabled()}`}
                onClick={() => dispatch(patchUserThunk({ firstName, lastName }))}
                data-cy={'personal-data-save-btn'}
            >
                {renderConfirmText()}
            </div>
        </div>
    );

    const submitForm = () => {
        if (firstName.trim().length > 0 && lastName.trim().length > 0) {
            dispatch(patchUserThunk({ firstName, lastName }));
        }
    };

    const renderContent = () => (
        <div data-cy={'personal-data-content'}>
            <TextFormControl
                id={'intl-msg:settingsDialog.tab.personalData.firstNameInputField'}
                labelId={'intl-msg:settingsDialog.tab.personalData.label.firstName'}
                value={firstName}
                validateInput={getNameValidation(intl)}
                onInputChange={(e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                onEnter={submitForm}
                readOnly={isInMaintenanceMode}
                testId={'first-name-input'}
            />
            <TextFormControl
                id={'intl-msg:settingsDialog.tab.personalData.lastNameInputField'}
                labelId={'intl-msg:settingsDialog.tab.personalData.label.lastName'}
                value={lastName}
                validateInput={getNameValidation(intl)}
                onInputChange={(e: ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                onEnter={submitForm}
                readOnly={isInMaintenanceMode}
                testId={'last-name-input'}
            />

            <PasswordResetField />
            {renderSaveButton()}
        </div>
    );

    return <>{isPending ? renderSpinner() : renderContent()}</>;
};
